import React, { useState, useEffect } from 'react';
import { useCreateAsset, useUpdateAsset } from '../../hooks/useAssets';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { AlertCircle } from 'lucide-react';
import type { AssetType, Asset } from '../../types/asset';

interface AssetFormProps {
  assetType: AssetType;
  asset: Asset | null;
  open: boolean;
  onClose: () => void;
}

const AssetForm: React.FC<AssetFormProps> = ({ assetType, asset, open, onClose }) => {
  const isEditing = !!asset;
  const createAsset = useCreateAsset();
  const updateAsset = useUpdateAsset();

  const initialFieldValues = assetType.fields.reduce((acc, field) => {
    acc[field.fieldId] = asset?.fieldValues[field.fieldId] || '';
    return acc;
  }, {} as Record<string, any>);

  const [fieldValues, setFieldValues] = useState(initialFieldValues);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (asset) {
      const values = assetType.fields.reduce((acc, field) => {
        acc[field.fieldId] = asset.fieldValues[field.fieldId] || '';
        return acc;
      }, {} as Record<string, any>);
      setFieldValues(values);
    }
  }, [asset, assetType.fields]);

  const validate = () => {
    const newErrors: Record<string, string> = {};

    assetType.fields.forEach(field => {
      const value = fieldValues[field.fieldId];
      
      if (field.required && (!value || value.toString().trim() === '')) {
        newErrors[field.fieldId] = `${field.fieldName} es requerido`;
      }

      if (value && field.fieldType === 'number' && isNaN(Number(value))) {
        newErrors[field.fieldId] = `${field.fieldName} debe ser un número`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    // Convert values to appropriate types
    const processedValues = assetType.fields.reduce((acc, field) => {
      const value = fieldValues[field.fieldId];
      
      if (field.fieldType === 'number' && value) {
        acc[field.fieldId] = Number(value);
      } else {
        acc[field.fieldId] = value;
      }
      
      return acc;
    }, {} as Record<string, any>);

    if (isEditing) {
      updateAsset.mutate(
        {
          assetId: asset.assetId,
          data: { fieldValues: processedValues },
        },
        {
          onSuccess: () => {
            onClose();
          },
          onError: () => {
            onClose();
          },
        }
      );
    } else {
      createAsset.mutate(
        {
          assetTypeId: assetType.assetTypeId,
          fieldValues: processedValues,
        },
        {
          onSuccess: () => {
            onClose();
          },
          onError: () => {
            onClose();
          },
        }
      );
    }
  };

  const handleFieldChange = (fieldId: string, value: any) => {
    setFieldValues({ ...fieldValues, [fieldId]: value });
  };

  const isPending = createAsset.isLoading || updateAsset.isLoading;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? `Editar ${assetType.nameSingular}` : `Crear ${assetType.nameSingular}`}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? `Actualiza la información del ${assetType.nameSingular.toLowerCase()}`
              : `Completa los datos para crear un nuevo ${assetType.nameSingular.toLowerCase()}`}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {assetType.fields.map((field) => (
            <div key={field.fieldId} className="space-y-2">
              <Label htmlFor={field.fieldId}>
                {field.fieldName}
                {field.required && <span className="text-red-500"> *</span>}
              </Label>
              
              {field.fieldType === 'text' && (
                <Input
                  id={field.fieldId}
                  type="text"
                  value={fieldValues[field.fieldId] || ''}
                  onChange={(e) => handleFieldChange(field.fieldId, e.target.value)}
                  className={errors[field.fieldId] ? 'border-red-500' : ''}
                />
              )}

              {field.fieldType === 'number' && (
                <Input
                  id={field.fieldId}
                  type="number"
                  value={fieldValues[field.fieldId] || ''}
                  onChange={(e) => handleFieldChange(field.fieldId, e.target.value)}
                  className={errors[field.fieldId] ? 'border-red-500' : ''}
                />
              )}

              {field.fieldType === 'date' && (
                <Input
                  id={field.fieldId}
                  type="date"
                  value={fieldValues[field.fieldId] || ''}
                  onChange={(e) => handleFieldChange(field.fieldId, e.target.value)}
                  className={errors[field.fieldId] ? 'border-red-500' : ''}
                />
              )}

              {errors[field.fieldId] && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors[field.fieldId]}
                </p>
              )}
            </div>
          ))}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending
                ? isEditing
                  ? 'Guardando...'
                  : 'Creando...'
                : isEditing
                ? 'Guardar Cambios'
                : `Crear ${assetType.nameSingular}`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AssetForm;
